/**
 * VerificationMaterialDialog — 写入 / 轮转回调验签材料（设计稿 PayerMax A4 写入弹窗 560、A5 轮转确认 720；
 * AIRWALLEX 沿用同款）。
 *
 * `PUT …/webhook-endpoints/:endpointId/verification-keys`（A8 §10.2）作用于整个商户账户组：
 *   - 组内尚无材料 → 「写入验签材料」：录入平台公钥即可；组内有已连接配置（材料随回调地址全部退役）时，
 *     按条件列出影响面——凭据公钥与新材料不同的配置会回到待验证，前端读不到凭据，不断言；
 *   - 组内已有材料 → 「轮转验签材料」：列出影响面（同组全部 App 配置与一起轮转的回调地址），提醒先在 PayerMax
 *     后台确认新公钥已生效；新公钥与某 App 凭据里的平台公钥不同时，该 App 回到待验证并在重新测试前停止下发。
 * AIRWALLEX 的验签材料是通知密钥（共享密钥，65 §2.2）：不属于凭据字段，写入 / 轮转不改写任何 App 配置，故不列配置
 * 影响面；按秘密遮蔽录入。称呼与录入形态取 `provider-channel-copy.ts`。
 * 材料只写：提交即清空输入框，响应只回份数与宽限截止；组内某配置凭据不可用（`CREDENTIAL_ROUTE_MISSING`）时
 * 点名该 App，整次轮转未生效。
 */

import type {
  PaymentProviderChannel,
  PaymentProviderVerificationKeyWriteResponse,
  PaymentProviderWebhookEndpointSummary,
} from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { AppRow } from '~/data/queries';
import { paymentProviderConfigApi } from '~/hooks/use-payment-provider-config';
import { Icons } from '~/lib/icons';
import { newIdempotencyKeyV7 } from '~/lib/idempotency-key';
import { AppIcon } from '~/ui/AppIcon';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { PasswordInput } from '~/ui/PasswordInput';
import { toast } from '~/ui/Toast';
import { ProviderNoticeBlock } from './ProviderNoticeBlock';
import {
  CONNECTION_STATUS_META,
  type MerchantGroup,
  chipClass,
  groupHasMaterial,
  normalizeKeyMaterial,
  providerPlatformOf,
  shortEndpointId,
} from './payment-provider-config-view';
import {
  type ProviderNotice,
  describeProviderError,
  noticeToastText,
  noticeToastTone,
} from './payment-provider-error-notice';
import { providerChannelCopy } from './provider-channel-copy';

function rotationConsequence(config: MerchantGroup['configs'][number]): {
  text: string;
  stops: boolean;
} {
  if (config.connection_status === 'connected' && config.is_active) {
    return { text: '回到待验证 · 立即停止下发', stops: true };
  }
  if (config.connection_status === 'unverified') return { text: '保持待验证', stops: false };
  return { text: '回到待验证', stops: false };
}

export function VerificationMaterialDialog({
  channel,
  group,
  endpoint,
  appsById,
  editBlockedReason,
  onClose,
  onDone,
  onRefresh,
  onEditApp,
}: {
  channel: PaymentProviderChannel;
  group: MerchantGroup;
  endpoint: PaymentProviderWebhookEndpointSummary;
  appsById: Map<string, AppRow>;
  /** 非空 = 「编辑 X 凭据」入口禁用的原因（只读或能力声明未读到） */
  editBlockedReason: string | null;
  onClose: () => void;
  onDone: (result: PaymentProviderVerificationKeyWriteResponse, rotated: boolean) => void;
  onRefresh: () => void;
  onEditApp: (appId: string) => void;
}) {
  // 组内已有材料 → 写入不同材料必然整组轮转（§10.2 规则 1 保证组内凭据公钥都等于现材料）。
  // 组内没有材料但有已连接配置（材料随回调地址全部退役）→ 只有凭据公钥与新材料不同的配置会回到待验证，
  // 前端读不到凭据，影响面按条件措辞展示，不断言会停止下发
  // 验签材料不属于凭据字段的通道（AIRWALLEX）：写入不动配置，没有配置影响面
  const copy = providerChannelCopy(channel);
  const materialTiedToCredential = copy.verificationCredentialField !== null;
  const rotation = groupHasMaterial(group.endpoints);
  const conditionalImpact =
    materialTiedToCredential &&
    !rotation &&
    group.configs.some((c) => c.connection_status === 'connected');
  const configImpact = materialTiedToCredential && (rotation || conditionalImpact);
  const showImpact = rotation || conditionalImpact;
  // 材料只在请求体出现一次：经 ref 交给 mutationFn，不作为 mutation variables 留在 MutationCache
  const pendingMaterial = useRef('');
  const [material, setMaterial] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKeyV7());
  const [notice, setNotice] = useState<ProviderNotice | null>(null);
  const groupLabel = `${group.merchantAccountId} · ${group.mode}`;
  const appName = (id: string) => appsById.get(id)?.name ?? id;
  const stopping = materialTiedToCredential
    ? group.configs.filter((c) => rotationConsequence(c).stops)
    : [];

  const write = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: () => {
      const value = pendingMaterial.current;
      pendingMaterial.current = '';
      return paymentProviderConfigApi.writeVerificationKey(
        channel,
        endpoint.endpoint_id,
        value,
        idempotencyKey,
      );
    },
    onSuccess: (result) => {
      onDone(result, rotation);
    },
    onError: (error) => {
      const described = describeProviderError(error, 'material', { appName, channel });
      if (described.rotateIdempotencyKey) setIdempotencyKey(newIdempotencyKeyV7());
      if (described.refresh) onRefresh();
      if (described.placement === 'toast') {
        toast(noticeToastText(described), { tone: noticeToastTone(described) });
        if (described.refresh) onClose();
      } else {
        setNotice(described);
      }
    },
  });

  const pending = write.isPending;
  const empty = copy.materialMultiline
    ? normalizeKeyMaterial(material) === ''
    : material.trim() === '';
  const submit = () => {
    if (empty || pending) return;
    pendingMaterial.current = material;
    // 材料只写：一经提交即清空输入框，不在本地留存
    setMaterial('');
    setNotice(null);
    write.mutate();
  };
  const close = () => {
    if (!pending) onClose();
  };
  const onMaterialChange = (next: string) => {
    setMaterial(next);
    setNotice(null);
    // 内容变了即新意图：换新幂等键，避免同键改内容撞 IDEMPOTENCY_KEY_MISMATCH
    setIdempotencyKey(newIdempotencyKeyV7());
  };

  return (
    <Dialog
      icon={rotation ? 'IconRefresh' : 'IconShield'}
      title={
        rotation ? (
          <>
            轮转验签材料 · <span className="mono">{groupLabel}</span>
          </>
        ) : (
          '写入验签材料'
        )
      }
      subtitle={
        rotation
          ? `换一把${copy.materialName}；作用于整个商户账户组，不只是这一个回调地址`
          : `把 ${copy.displayName} ${copy.materialName}写入该回调地址，用于校验回调签名`
      }
      width={showImpact ? 720 : 560}
      onClose={close}
      footer={
        <>
          <div
            style={{
              flex: 1,
              fontSize: 11,
              color: showImpact && stopping.length > 0 ? 'var(--warn)' : 'var(--fg-muted)',
            }}
          >
            {stopping.length > 0 && rotation
              ? `${stopping.map((c) => appName(c.app_id)).join('、')} 将在重新测试前停止下发 ${copy.displayName}`
              : stopping.length > 0 && conditionalImpact
                ? `新公钥与 ${stopping.map((c) => appName(c.app_id)).join('、')} 凭据里的不同时，它们将在重新测试前停止下发`
                : '提交后输入框立即清空'}
          </div>
          <button type="button" className="btn" disabled={pending} onClick={close}>
            取消
          </button>
          <button
            type="button"
            className={rotation ? 'btn' : 'btn btn-accent'}
            disabled={pending || empty}
            onClick={submit}
            style={
              rotation && !pending && !empty
                ? { background: 'var(--warn)', borderColor: 'var(--warn)', color: 'white' }
                : undefined
            }
          >
            {pending ? '写入中…' : rotation ? '确认轮转' : '写入'}
          </button>
        </>
      }
    >
      <DialogBody gap={14}>
        {rotation ? (
          <div
            className="hstack"
            style={{
              gap: 10,
              alignItems: 'flex-start',
              padding: '10px 12px',
              background: 'var(--warn-soft)',
              borderRadius: 'var(--radius)',
            }}
          >
            <Icons.IconAlert size={14} style={{ color: 'var(--warn)', marginTop: 2 }} />
            <div style={{ fontSize: 11.5, lineHeight: 1.55 }}>
              先在{copy.consoleName}确认<b>新{copy.materialName}已经生效</b>
              。否则新回调会用旧密钥签名，而旧材料到宽限截止后不再参与验签。
              {!materialTiedToCredential && '轮转不改动组内 App 配置与连接状态。'}
            </div>
          </div>
        ) : (
          <div className="hstack" style={{ gap: 6, fontSize: 11.5, color: 'var(--fg-muted)' }}>
            商户组{' '}
            <span className="mono" style={{ color: 'var(--fg)' }}>
              {groupLabel}
            </span>
            <span>·</span>
            endpoint{' '}
            <span className="mono" style={{ color: 'var(--fg)' }}>
              {shortEndpointId(endpoint.endpoint_id)}
            </span>
          </div>
        )}

        <div>
          <label
            htmlFor="provider-verification-material"
            style={{ fontSize: 12.5, fontWeight: 500 }}
          >
            {rotation ? `新${copy.materialName}` : copy.materialName}
            <span style={{ color: 'var(--neg)', marginLeft: 3 }}>*</span>
          </label>
          {copy.materialSecret ? (
            <PasswordInput
              id="provider-verification-material"
              className="input mono"
              autoComplete="off"
              spellCheck={false}
              value={material}
              disabled={pending}
              placeholder={copy.materialPlaceholder}
              aria-invalid={notice ? true : undefined}
              aria-describedby={notice ? 'provider-verification-material-err' : undefined}
              onChange={(e) => onMaterialChange(e.target.value)}
              style={{ marginTop: 6 }}
            />
          ) : (
            <textarea
              id="provider-verification-material"
              className="input mono"
              rows={rotation ? 4 : 5}
              spellCheck={false}
              autoComplete="off"
              value={material}
              disabled={pending}
              placeholder={copy.materialPlaceholder}
              aria-invalid={notice ? true : undefined}
              aria-describedby={notice ? 'provider-verification-material-err' : undefined}
              onChange={(e) => onMaterialChange(e.target.value)}
              style={{ marginTop: 6, fontSize: 11, width: '100%', boxSizing: 'border-box' }}
            />
          )}
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
            {materialTiedToCredential
              ? `接受带或不带 PEM 头尾 · 与凭据里的 ${copy.verificationCredentialField} 是同一把 · 写入后不回显`
              : `取自${copy.consoleName}的 webhook 订阅 · 不属于凭据 · 写入后不回显`}
          </div>
          {notice && (
            <div id="provider-verification-material-err" style={{ marginTop: 8 }}>
              <ProviderNoticeBlock
                notice={notice}
                actions={
                  notice.kind === 'route-missing' && notice.appId ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={editBlockedReason !== null}
                      title={editBlockedReason ?? undefined}
                      onClick={() => onEditApp(notice.appId as string)}
                    >
                      编辑 {appName(notice.appId)} 凭据
                    </button>
                  ) : null
                }
              />
            </div>
          )}
        </div>

        {showImpact && (
          <>
            {configImpact && (
              <div>
                <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500 }}>影响面 · 同组 App 配置</span>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                    {group.configs.length}
                  </span>
                </div>
                <div className="card-flat" style={{ overflow: 'hidden' }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>App</th>
                        <th>当前</th>
                        <th>新公钥与其凭据不同时</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.configs.map((config) => {
                        const app = appsById.get(config.app_id);
                        const meta = CONNECTION_STATUS_META[config.connection_status];
                        const consequence = rotationConsequence(config);
                        return (
                          <tr key={config.app_id}>
                            <td>
                              <span className="hstack" style={{ gap: 8 }}>
                                <AppIcon app={app} label={app ? undefined : '?'} size="sm" />
                                {app?.name ?? config.app_id}
                                <span className="chip mono" style={{ fontSize: 9.5 }}>
                                  {providerPlatformOf(app) ?? '平台未知'}
                                </span>
                              </span>
                            </td>
                            <td>
                              <span className="hstack" style={{ gap: 4 }}>
                                <span className={chipClass(meta.tone)} style={{ fontSize: 9.5 }}>
                                  {meta.label}
                                </span>
                                <span
                                  className={config.is_active ? 'chip chip-pos' : 'chip'}
                                  style={{ fontSize: 9.5 }}
                                >
                                  {config.is_active ? '已启用' : '未启用'}
                                </span>
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  fontSize: 12,
                                  color: consequence.stops ? 'var(--neg)' : 'var(--fg-muted)',
                                  fontWeight: consequence.stops ? 500 : undefined,
                                }}
                              >
                                {consequence.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}
                >
                  新公钥与某个 App 凭据里的平台公钥相同时，该 App 状态不变。重新测试前，回到待验证的
                  App 不会下发 {copy.displayName}。
                </div>
              </div>
            )}

            <div>
              <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                  {rotation ? '一起轮转的回调地址' : '一起写入的回调地址'}
                </span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                  {group.endpoints.length}
                </span>
              </div>
              <div className="vstack" style={{ gap: 4, fontSize: 11.5 }}>
                {group.endpoints.map((e) => (
                  <div key={e.endpoint_id} className="hstack" style={{ gap: 8 }}>
                    <span className="mono">{shortEndpointId(e.endpoint_id)}</span>
                    <span
                      className={e.is_active ? 'chip chip-pos' : 'chip'}
                      style={{ fontSize: 9.5 }}
                    >
                      {e.is_active ? '启用中' : '已停用'}
                    </span>
                    <span style={{ color: 'var(--fg-muted)' }}>
                      {e.verifying_material_count > 0
                        ? `${e.verifying_material_count} 份材料，旧材料按 retire_after 继续参与验签`
                        : '尚无材料，写入后与组内一致'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogBody>
    </Dialog>
  );
}
